/**
 * <trustpilot-rating sku="123,456" businessunit-id="..." hidden>
 *
 * Rating badge (product card "4.5 ★", product page stars + "N avis") — markup in snippets/trustpilot-rating.liquid — fed by Trustpilot product reviews. Trustpilot does not write ratings to the
 * Shopify reviews metafields, so the badge fetches the product summary client-side from the TrustBox data endpoint
 * (the one Trustpilot's own widgets use; CORS-enabled and CDN-cached for 30 min).
 *
 * Behaviour:
 *  - Stays hidden until a rating is found: products without reviews show nothing.
 *  - Only fetches once the card is close to the viewport (IntersectionObserver).
 *  - Concurrent requests are capped, and results are cached per SKU list in memory + sessionStorage.
 *  - Works for cards injected later (Nosto dynamic cards, filters, infinite scroll) since it is a custom element.
 */
(() => {
  if (customElements.get('trustpilot-rating')) return;

  // Trustpilot "Micro Star" template: returns the productReviewsSummary for the given SKU(s).
  const ENDPOINT = 'https://widget.trustpilot.com/trustbox-data/54d39695764ea907c0f34825';
  const MAX_CONCURRENT = 4;
  const STORAGE_KEY = 'tp-rating:';
  const STORAGE_TTL = 30 * 60 * 1000;

  const memoryCache = new Map();
  const queue = [];
  let active = 0;

  const readStorage = (key) => {
    try {
      const entry = JSON.parse(sessionStorage.getItem(STORAGE_KEY + key));
      return entry && Date.now() - entry.t < STORAGE_TTL ? entry.v : undefined;
    } catch (e) {
      return undefined;
    }
  };

  const writeStorage = (key, value) => {
    try {
      sessionStorage.setItem(STORAGE_KEY + key, JSON.stringify({ t: Date.now(), v: value }));
    } catch (e) {
      // Storage full or blocked: the in-memory cache is enough.
    }
  };

  const pump = () => {
    while (active < MAX_CONCURRENT && queue.length) {
      const job = queue.shift();
      active++;
      job().finally(() => {
        active--;
        pump();
      });
    }
  };

  const fetchSummary = (businessUnitId, sku) => {
    const key = `${businessUnitId}:${sku}`;
    if (memoryCache.has(key)) return memoryCache.get(key);

    const stored = readStorage(key);
    if (stored !== undefined) {
      const promise = Promise.resolve(stored);
      memoryCache.set(key, promise);
      return promise;
    }

    const promise = new Promise((resolve) => {
      queue.push(() => {
        const url = `${ENDPOINT}?businessUnitId=${encodeURIComponent(businessUnitId)}&locale=fr-FR&sku=${encodeURIComponent(sku)}`;
        return fetch(url)
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            const summary = data && data.productReviewsSummary;
            const value = summary && summary.numberOfReviews && summary.numberOfReviews.total > 0
              ? { average: summary.starsAverage, count: summary.numberOfReviews.total }
              : null;
            writeStorage(key, value);
            resolve(value);
          })
          .catch(() => resolve(null));
      });
      pump();
    });

    memoryCache.set(key, promise);
    return promise;
  };

  // The badge itself is display:none (hidden) until a rating is found, and IntersectionObserver never reports a
  // display:none element as intersecting. We therefore observe the visible card around it and map it back.
  const targets = new WeakMap();
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          (targets.get(entry.target) || []).forEach((badge) => badge.load());
          targets.delete(entry.target);
        });
      }, { rootMargin: '300px 0px' })
    : null;

  const anchorOf = (badge) => badge.closest('product-card, .product-card') || badge.parentElement || badge;

  class TrustpilotRating extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      if (!observer) return this.load();

      this._anchor = anchorOf(this);
      const badges = targets.get(this._anchor) || [];
      badges.push(this);
      targets.set(this._anchor, badges);
      observer.observe(this._anchor);
    }

    disconnectedCallback() {
      if (!this._anchor || this._loaded) return;
      const badges = (targets.get(this._anchor) || []).filter((badge) => badge !== this);
      if (badges.length) {
        targets.set(this._anchor, badges);
      } else {
        targets.delete(this._anchor);
        observer.unobserve(this._anchor);
      }
    }

    load() {
      const sku = this.getAttribute('sku');
      const businessUnitId = this.getAttribute('businessunit-id');
      if (this._loaded || !sku || !businessUnitId) return;
      this._loaded = true;

      fetchSummary(businessUnitId, sku).then((rating) => {
        if (!rating) return;
        const average = Number(rating.average).toFixed(1);
        const value = this.querySelector('[data-rating-value]');
        const fill = this.querySelector('[data-rating-fill]');
        const count = this.querySelector('[data-rating-count]');
        if (value) value.textContent = average;
        if (fill) fill.style.width = `${Math.min(100, Math.max(0, (rating.average / 5) * 100))}%`;
        if (count) count.textContent = `${rating.count} avis`;
        this.setAttribute('aria-label', `Note ${average.replace('.', ',')} sur 5 (${rating.count} avis)`);
        this.hidden = false;
      });
    }
  }

  customElements.define('trustpilot-rating', TrustpilotRating);
})();
